"use client";
import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/navigation";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckIcon from "@mui/icons-material/Check";
import { Check } from "lucide-react";
import NextLink from "next/link";
const OMNI_FEATURES = ["feature1", "feature2", "feature3", "feature4"];

const OmniAgentPage = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const router = useRouter();

  return (
    <Box
    id="omni-agent-section"
      sx={{
        backgroundImage:
          "url(https://getgeek-website-assets.b-cdn.net/OmniNewBG.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        overflow: "hidden",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 6, md: 10 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 4, md: 7 },
          }}
        >
          {/* Left column: Agent avatar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flexShrink: 0,
              width: { xs: "auto", md: 200 },
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 600,
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                color: "#DCC49B",
                mb: 2,
                textTransform: "uppercase",
              }}
            >
              {t("omniPage.agentLabel", "Agent 06")}
            </Typography>

            <Box
              component="img"
              src="https://getgeek-website-assets.b-cdn.net/OmniLogoNew.webp"
              alt="Omni"
              sx={{
                width: { xs: "110px", sm: "130px", md: "150px" },
                height: "auto",
                objectFit: "cover",
                display: "block",
                mb: 2,
              }}
            />

            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.15rem" },
                letterSpacing: "0.15em",
                color: "#101828",
                textAlign: "center",
              }}
            >
              {t("omniPage.agentName")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                textAlign: "center",
                fontWeight: 500,
                color: "#C07C0A",
                mb: 1.2,
              }}
            >
              {t("omniPage.agentRole")}
            </Typography>

            <Box
              sx={{
                width: 28,
                height: 3,
                borderRadius: "2px",
                background: "linear-gradient(90deg, #C07C0A 0%, #C07C0A 100%)",
              }}
            />
          </Box>

          {/* Right column: Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Main Heading */}
            <Typography
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 700,
                fontSize: { xs: "1.4rem", sm: "1.7rem", md: "2.1rem" },
                color: "#101828",
                lineHeight: 1.3,
                mb: 3,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("omniPage.headingPart1")}{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #C07C0A 0%, #C07C0A 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t("omniPage.headingPart2")}
              </Box>
            </Typography>

            {/* Alert Banner */}
            <Box
              sx={{
                mb: 3,
                borderLeft: isArabic ? "none" : "3px solid #C07C0A",
                borderRight: isArabic ? "3px solid #E17100" : "none",
                pl: isArabic ? 0 : 1.8,
                pr: isArabic ? 1.8 : 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.92rem" },
                  color: "#14122B",
                  lineHeight: 1.85,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    backgroundColor: "#FFF4DD",
                    fontWeight: 700,
                    color: "#101828",
                    px: 0.6,
                    py: 0.15,
                    borderRadius: "4px",
                  }}
                >
                  {t("omniPage.alertBold1")}
                </Box>{" "}
                {t("omniPage.alertText1")}{" "}
                <Box
                  component="span"
                  sx={{
                    backgroundColor: "#FFF4DD",
                    fontWeight: 700,
                    color: "#101828",
                    px: 0.6,
                    py: 0.15,
                    borderRadius: "4px",
                  }}
                >
                  {t("omniPage.alertBold2")}
                </Box>{" "}
                {t("omniPage.alertText2")} {t("omniPage.alertText3")}
              </Typography>
            </Box>

            {/* Description paragraphs */}
            {["desc1", "desc2", "desc3"].map((key) => {
              const value = t(`omniPage.${key}`);
              if (!value) return null;
              return (
                <Typography
                  key={key}
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.92rem" },
                    color: "#6B7280",
                    lineHeight: 1.85,
                    mb: 1.8,
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {value}
                </Typography>
              );
            })}

            {/* What OMNI does for you */}
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: 700,
                fontSize: { xs: "0.85rem", sm: "0.92rem", md: "0.98rem" },
                color: "#101828",
                mt: { xs: 2, sm: 2.5 },
                mb: 1.8,
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("omniPage.featuresTitle")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 1.2, sm: 1.5 },
                mb: 3,
              }}
            >
              {OMNI_FEATURES.map((key) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    backgroundColor: "#FFFBEB",
                    border: "1px solid #FCE8B8",
                    borderRadius: "10px",
                    p: 1.4,
                  }}
                >

                  <Check
                    size={16}
                    strokeWidth={3}   // 2 = normal, 3 = bold, 4 = extra bold
                    color="#E17100"
                    style={{ flexShrink: 0, marginTop: 3 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      color: "#374151",
                      lineHeight: 1.6,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {t(`omniPage.features.${key}`)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Highlight box */}
            <Box
              sx={{
                mb: 4,
                backgroundColor: "#FFFBEB",
                border: "1px solid #FCE8B8",
                borderRadius: "10px",
                p: 1.8,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.75rem", sm: "0.82rem", md: "0.88rem" },
                  color: "#101828",
                  lineHeight: 1.85,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {t("omniPage.highlightBold1")}
                </Box>{" "}
                {t("omniPage.highlightText1")}{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {t("omniPage.highlightBold2")}
                </Box>{" "}
                {t("omniPage.highlightText2")}
              </Typography>
            </Box>

            {/* CTA Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                pb: { xs: 4, sm: 5 },
              }}
            >
              <NextLink href="/ai-agents/omni" passHref legacyBehavior>
                <Button
                  component="a"
                  rel="noopener noreferrer"
                  sx={{
                    background: "linear-gradient(90deg, #D4790B 0%, #D4790B 100%)",
                    color: "#fff",
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 600,
                    fontSize: { xs: "0.82rem", sm: "0.88rem" },
                    px: { xs: 3, sm: 3.5 },
                    py: { xs: 1.1, sm: 1.3 },
                    borderRadius: "50px",
                    textTransform: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "linear-gradient(90deg, #D4790B 0%, #D4790B 100%)",
                      transform: "scale(1.03)",
                    },
                    "&:active": { transform: "scale(0.98)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <span>{t("omniPage.ctaButton")}</span>
                    {isArabic ? (
                      <ArrowBackIosNewIcon sx={{ fontSize: "0.75rem" }} />
                    ) : (
                      <ArrowForwardIosIcon sx={{ fontSize: "0.75rem" }} />
                    )}
                  </Box>
                </Button>
              </NextLink>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default OmniAgentPage;